import { Box, FormControlLabel, FormHelperText } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useContext, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import ReactMarkdown from 'react-markdown'
import AppContext from '../../../context/appContext'
import { setDirtyForm } from '../../../services/appPreferences/dirtyFormStorage'
import { dateBoundaries } from '../../../utils/dataCollection/dateBoundaries'
import { dateValidations } from '../../../utils/dataCollection/dateValidations'
import { DATE_VALIDATION_CONTEXT_KEYS, DynamicFormYearsContext } from '../dynamicFormYearsContext'

const CONTEXT_FIELD_MAPPING = {
  metricYear: 'year',
  lastFarmSubdivisionYear: 'lastFarmSubdivisionYear',
}

const DateFieldWrapper = ({ control, formField, readOnly, fieldArrayName, fieldArrayIndex }) => {
  const { watch, getValues } = useFormContext()
  const { setPartialChanges } = useContext(AppContext)
  const yearsCtx = useContext(DynamicFormYearsContext)
  const { year: ctxYear, lastFarmSubdivisionYear: ctxLastFarmSubdivisionYear } = yearsCtx

  const boundariesValidation = formField?.validations?.find(
    validation => validation?.type === 'boundaries',
  )
  const boundaryValidationStartDateRange =
    boundariesValidation?.name === 'formFieldsRange'
      ? watch(boundariesValidation.formFields.startDate.value)
      : null
  const boundaryValidationEndDateRange =
    boundariesValidation?.name === 'formFieldsRange'
      ? watch(boundariesValidation.formFields.endDate.value)
      : null

  const currentDateBoundaries = useMemo(() => {
    if (boundariesValidation) {
      return dateBoundaries[boundariesValidation.name]({
        year: ctxYear,
        startDate: boundaryValidationStartDateRange,
        endDate: boundaryValidationEndDateRange,
        lastFarmSubdivisionYear: ctxLastFarmSubdivisionYear,
      })
    }
    return null
  }, [
    boundariesValidation,
    ctxYear,
    boundaryValidationStartDateRange,
    boundaryValidationEndDateRange,
    ctxLastFarmSubdivisionYear,
  ])

  return (
    <FormControlLabel
      control={
        <Controller
          control={control}
          defaultValue={formField.defaultValue}
          name={`${formField.name}`}
          render={({ field, fieldState }) => (
            <Box sx={{ marginLeft: 1.5, width: '100%' }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  disabled={readOnly}
                  format="DD/MM/YYYY"
                  label={formField.placeholder}
                  maxDate={currentDateBoundaries?.maxDate}
                  minDate={currentDateBoundaries?.minDate}
                  slotProps={{
                    textField: {
                      sx: { width: '100%' },
                      error: Boolean(fieldState.error),
                    },
                  }}
                  value={field.value ? dayjs(field.value, 'YYYY-MM-DD') : null}
                  onChange={value => {
                    setPartialChanges(true)
                    field.onChange(value.format('YYYY-MM-DD'))
                    if (!readOnly) setDirtyForm(true)
                  }}
                />
              </LocalizationProvider>
              <FormHelperText error={Boolean(fieldState.error)} sx={{ px: 1.5 }}>
                <ReactMarkdown>
                  {fieldState.error ? fieldState.error.message : formField.helperText}
                </ReactMarkdown>
              </FormHelperText>
            </Box>
          )}
          rules={{
            required: formField.required ? ' Campo requerido' : false,
            validate: value => {
              if (!formField?.validations) return true

              const resolveFieldValue = fieldName => {
                if (DATE_VALIDATION_CONTEXT_KEYS.includes(fieldName)) {
                  return yearsCtx[CONTEXT_FIELD_MAPPING[fieldName]]
                }
                if (fieldArrayName) {
                  return getValues(fieldArrayName)[fieldArrayIndex]?.[fieldName]
                }
                return getValues(fieldName)
              }

              const validationError = formField.validations.reduce((error, formFieldValidation) => {
                if (error) return error

                const validation = dateValidations[formFieldValidation.name]
                if (!validation) return null

                let formFieldsValues = {}
                if (formFieldValidation?.formFields) {
                  formFieldsValues = Object.entries(formFieldValidation.formFields).reduce(
                    (acc, [validationKey, fieldInfo]) => ({
                      ...acc,
                      [validationKey]: {
                        ...fieldInfo,
                        value: resolveFieldValue(fieldInfo.value),
                      },
                    }),
                    {},
                  )
                }

                const validationResult = validation(value, formFieldsValues)
                return validationResult !== true ? validationResult : null
              }, null)

              return validationError || true
            },
          }}
        />
      }
      sx={{ width: '100%' }}
    />
  )
}

export default DateFieldWrapper
